import React, { useLayoutEffect, useRef } from "react";
import { components as selectComponents } from "react-select";

/**
 * MenuList for paginated react-select instances, supporting touch scrolling
 * and preserving the scroll position when more options are appended.
 */
const PaginatedSelectMenuList = (props) => {
  const menuListRef = useRef(null);
  const scrollTopRef = useRef(0);
  const optionsLengthRef = useRef(props.options.length);

  /**
   * Sets the reference to the menu list DOM element and forwards it to react-select's innerRef.
   * @param {HTMLElement|null} element - The DOM element reference for the menu list.
   */
  const setMenuListRef = (element) => {
    menuListRef.current = element;
    if (typeof props.innerRef === "function") {
      props.innerRef(element);
    } else if (props.innerRef) {
      props.innerRef.current = element;
    }
  };

  /**
   * Handles the scroll event for the menu list, updating the scroll position reference
   * and triggering the load more callback when nearing the bottom.
   * @param {object} eventScroll - The scroll event object.
   */
  const handleMenuListScroll = (eventScroll) => {
    const { scrollTop, scrollHeight, clientHeight } = eventScroll.currentTarget;
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    scrollTopRef.current = scrollTop;

    props.innerProps?.onScroll?.(eventScroll);
    if (distanceToBottom <= 20) {
      props.selectProps.onPaginatedSelectLoadMore();
    }
  };

  /**
   * Preserves the scroll position when the list of options changes.
   * This ensures that the user does not lose their place when more options are appended.
   */
  useLayoutEffect(() => {
    if (optionsLengthRef.current !== props.options.length && menuListRef.current) {
      menuListRef.current.scrollTop = scrollTopRef.current;
    }
    optionsLengthRef.current = props.options.length;
  }, [props.options.length]);

  return (
    <selectComponents.MenuList
      {...props}
      innerRef={setMenuListRef}
      innerProps={{
        ...props.innerProps,
        onScroll: handleMenuListScroll,
      }}
    />
  );
};

export default PaginatedSelectMenuList;
